import { useEffect } from 'react';

/** Origen público del sitio. Usado para construir canonical y og:url. */
export const SITE_URL = 'https://www.docenlinea.com.co';

/**
 * Crea o actualiza una etiqueta de <head> y devuelve la función que
 * restaura su estado previo: si la etiqueta ya existía se repone su valor
 * anterior, y si la creamos nosotros se elimina al desmontar.
 */
function manageTag({ selector, attr, value, create }) {
  if (!value) return () => {};

  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    el.setAttribute(attr, value);
    document.head.appendChild(el);
    return () => el.remove();
  }

  const prev = el.getAttribute(attr);
  el.setAttribute(attr, value);
  return () => {
    if (prev === null) el.removeAttribute(attr);
    else el.setAttribute(attr, prev);
  };
}

const metaByName = (name, value) => manageTag({
  selector: `meta[name="${name}"]`,
  attr: 'content',
  value,
  create: () => {
    const el = document.createElement('meta');
    el.setAttribute('name', name);
    return el;
  },
});

const metaByProperty = (property, value) => manageTag({
  selector: `meta[property="${property}"]`,
  attr: 'content',
  value,
  create: () => {
    const el = document.createElement('meta');
    el.setAttribute('property', property);
    return el;
  },
});

/**
 * Hook simple para setear los metadatos SEO por página, sin dependencias
 * externas (no usa react-helmet): <title>, description, canonical y las
 * tarjetas Open Graph / Twitter. Restaura los valores previos al desmontar
 * para no afectar otras páginas.
 *
 * @param {string} title
 * @param {string} description
 * @param {string} [canonicalPath] Ruta absoluta del sitio, ej. '/pacientes'.
 *   Si se omite no se emite canonical ni og:url.
 */
export default function useSeoMeta(title, description, canonicalPath) {
  useEffect(() => {
    const cleanups = [];

    if (title) {
      const prevTitle = document.title;
      document.title = title;
      cleanups.push(() => { document.title = prevTitle; });
    }

    const canonicalUrl = canonicalPath ? `${SITE_URL}${canonicalPath}` : null;

    cleanups.push(metaByName('description', description));
    cleanups.push(metaByName('twitter:title', title));
    cleanups.push(metaByName('twitter:description', description));
    cleanups.push(metaByProperty('og:title', title));
    cleanups.push(metaByProperty('og:description', description));
    cleanups.push(metaByProperty('og:url', canonicalUrl));
    cleanups.push(manageTag({
      selector: 'link[rel="canonical"]',
      attr: 'href',
      value: canonicalUrl,
      create: () => {
        const el = document.createElement('link');
        el.setAttribute('rel', 'canonical');
        return el;
      },
    }));

    return () => cleanups.reverse().forEach((restore) => restore());
  }, [title, description, canonicalPath]);
}
